import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { Address } from "@/services/backend/addresses/types/address";
import {
  ByAddress,
  ByAddressList,
} from "@/services/backend/buildings/types/by-address";
import { Filter } from "@/shared/lib/filter/filter";

/**
 * The buildings of a customer, grouped by the address they share. A group is not
 * an entity of its own — it is the buildings that sit at one address — so every
 * write here changes buildings, and the "Building" tags are invalidated with it.
 */
const buildingByAddressApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["Building", "BuildingByAddress"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      buildingsByAddressShow: build.query<ByAddress, ShowByAddressRequest>({
        query: (request) => ({
          url: `/buildings/by-address`,
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: (_result, _error, request) => [
          { type: "BuildingByAddress", id: request.addressKey },
        ],
      }),
      buildingsByAddressList: build.query<ByAddressList, ListByAddressRequest>({
        query: (request) => ({
          url: `/buildings/by-address/list`,
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: (_result, _error, request) => [
          { type: "BuildingByAddress", id: request.customer?.values?.[0] },
        ],
      }),
      buildingsByAddressBulkUpdate: build.mutation<
        ByAddress,
        BulkUpdateAddressRequest
      >({
        query: (request) => ({
          url: `/buildings/by-address`,
          method: "PATCH",
          body: request,
        }),
        // The buildings leave the group they were in and join the one at the new
        // address, so no address group survives the move untouched.
        invalidatesTags: (_result, _error, request) => [
          ...request.buildingIds.map((id) => ({
            type: "Building" as const,
            id,
          })),
          { type: "Building" as const, id: "LIST" },
          "BuildingByAddress" as const,
        ],
      }),
    }),
  });

export const {
  useBuildingsByAddressShowQuery,
  useBuildingsByAddressListQuery,
  useBuildingsByAddressBulkUpdateMutation,
} = buildingByAddressApi;

/**
 * An addressKey is derived from the address alone, so customers can share one.
 * The customer filter is what keeps the group to a single customer, and the
 * backend requires it.
 */
export interface ShowByAddressRequest {
  company?: Filter<string>;
  customer: Filter<string>;
  addressKey: string;
}

export interface ListByAddressRequest {
  company?: Filter<string>;
  customer: Filter<string>;
  addressKey?: Filter<string>;
}

/** Moves every named building to the address, in one request. */
export interface BulkUpdateAddressRequest {
  buildingIds: string[];
  address: Address;
}
